// https://stackoverflow.com/questions/62210286/declare-type-with-react-useimperativehandle
// https://github.com/jaredpalmer/formik/issues/1603#issuecomment-575669249

import {
  Box,
  Button,
  HStack,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  FieldArray as FormikFieldArray,
  Form as FormikForm,
  Formik,
  FormikHelpers,
} from "formik";
import React from "react";
import * as Yup from "yup";
import { FormInputControl, FormSingleCheckbox } from "../../components/forms";
import { ISelectOption } from "../../components/forms/SelectControl";
import { MaterialIcon } from "../../components/shared/MaterialIcon";
import {
  ENCRYPT_LOGIC_SELECT,
  ENCRYPT_OPERATORS,
  IEncryptQuery,
  initialEncryptQueryValues,
} from "../registries/models/Encrypt";
import { FormQueryConditionPill } from "./FormQueryConditionPill";
import { FormQueryJoinOnPill } from "./FormQueryJoinOnPill";
import { FormQueryOrderByPill } from "./FormQueryOrderByPill";
import { FormQuerySelectPill } from "./FormQuerySelectPill";

const EncryptQueryFormsSchema = Yup.object().shape({
  selects: Yup.array(
    Yup.object().shape({
      columnName: Yup.string().required("Select one"),
      asValue: Yup.string().nullable(),
      isDistinct: Yup.boolean(),
    })
  ),
  conditions: Yup.array(
    Yup.object().shape({
      logic: Yup.string(),
      field: Yup.string().required("Select one"),
      operator: Yup.string().required("Select one"),
      search: Yup.string().required("Add value"),
    })
  ),
  orderBy: Yup.array(Yup.string()),
  joinOn: Yup.array(Yup.string()),
  isSortDesc: Yup.boolean(),
  limit: Yup.string().nullable(),
});

export type EncryptFormHandle = {
  submitForm: () => void;
};

interface IProps {
  formValues?: IEncryptQuery;
  onSubmit: (data: IEncryptQuery) => Promise<void>;
  columns: ISelectOption[];
}

export const EncryptQueryForm = React.forwardRef<EncryptFormHandle, IProps>(
  ({ formValues, onSubmit, columns }, ref) => {
    React.useImperativeHandle(ref, () => ({
      // start() has type inferrence here
      submitForm() {
        bagRef.current.submitForm();
      },
    }));

    const [formData, setFormData] = React.useState<IEncryptQuery>(
      initialEncryptQueryValues
    );

    const bagRef = React.useRef<any>();

    const selectColumns = [{ label: "", value: "" }, ...columns];

    const handleOnSubmit = async (
      values: IEncryptQuery,
      actions: FormikHelpers<IEncryptQuery>
    ) => {
      actions.setStatus();
      actions.setSubmitting(true);

      try {
        // Pass up logic
        await onSubmit(values);
      } catch (error) {
        // Error submitting form, set status for dispaly
        actions.setStatus("Failed to query. Please try again.");
      }

      actions.setSubmitting(false);
    };

    React.useEffect(() => {
      if (formValues) setFormData(formValues);
    }, [formValues]);

    return (
      <Formik
        innerRef={bagRef}
        initialValues={formData}
        validationSchema={EncryptQueryFormsSchema}
        onSubmit={(values, actions) => {
          handleOnSubmit(values, actions);
        }}
        enableReinitialize={true}
      >
        {(props) => {
          return (
            <FormikForm>
              <Stack spacing={8}>
                <Stack borderWidth={1} p={6}>
                  <Text textStyle={"title"}>Select</Text>

                  <FormikFieldArray
                    name="selects"
                    validateOnChange={true}
                    render={(arrayHelpers) => (
                      <Stack spacing={6}>
                        <Stack spacing={4}>
                          {props.values.selects &&
                            props.values.selects.length > 0 &&
                            props.values.selects.map((email, index) => (
                              <Box key={index} alignItems="flex-start">
                                <FormQuerySelectPill
                                  name={`selects.${index}`}
                                  onRemove={() => arrayHelpers.remove(index)}
                                  columns={selectColumns}
                                />
                              </Box>
                            ))}
                        </Stack>

                        <Box>
                          <Button
                            isDisabled={props.values.selects.length > 3}
                            size="sm"
                            leftIcon={
                              <MaterialIcon name="add" fontSize="1rem" />
                            }
                            onClick={() =>
                              arrayHelpers.push({
                                columnName: selectColumns[0].value,
                                asValue: "",
                              })
                            }
                          >
                            Add select
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  ></FormikFieldArray>
                </Stack>
{/*
                <Stack borderWidth={1} p={6}>
                  <Text textStyle={"title"}>Join On</Text>

                  <FormikFieldArray
                    name="joinOn"
                    validateOnChange={true}
                    render={(arrayHelpers) => (
                      <Stack spacing={6}>
                        <Wrap spacing={4}>
                          {props.values.joinOn &&
                            props.values.joinOn.length > 0 &&
                            props.values.joinOn.map((email, index) => (
                              <WrapItem key={index} alignItems="flex-start">
                                <FormQueryJoinOnPill
                                  orderNumber={index + 1}
                                  name={`joinOn.${index}`}
                                  onRemove={() => arrayHelpers.remove(index)}
                                  columns={columns}
                                />
                              </WrapItem>
                            ))}
                        </Wrap>

                        <Box>
                          <Button
                            isDisabled={!!props.values.joinOn.length}
                            size="sm"
                            leftIcon={
                              <MaterialIcon name="add" fontSize="1rem" />
                            }
                            onClick={() => arrayHelpers.push("")}
                          >
                            Add Join
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  ></FormikFieldArray>
                </Stack>
*/}
                <Stack borderWidth={1} p={6}>
                  <Text textStyle={"title"}>Where</Text>

                  <FormikFieldArray
                    name="conditions"
                    validateOnChange={true}
                    render={(arrayHelpers) => (
                      <Stack spacing={6}>
                        <Stack spacing={4}>
                          {props.values.conditions &&
                            props.values.conditions.length > 0 &&
                            props.values.conditions.map((email, index) => (
                              <Box key={index} alignItems="flex-start">
                                <FormQueryConditionPill
                                  isShowLogic={index > 0}
                                  name={`conditions.${index}`}
                                  onRemove={() => arrayHelpers.remove(index)}
                                  columns={selectColumns}
                                />
                              </Box>
                            ))}
                        </Stack>

                        <Box>
                          <Button
                            isDisabled={props.values.conditions.length > 1}
                            size="sm"
                            leftIcon={
                              <MaterialIcon name="add" fontSize="1rem" />
                            }
                            onClick={() =>
                              arrayHelpers.push({
                                field: "",
                                operator: ENCRYPT_OPERATORS[0].value,
                                search: "",
                                logic: ENCRYPT_LOGIC_SELECT[0].value,
                              })
                            }
                          >
                            Add Condition
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  ></FormikFieldArray>
                </Stack>

                <Stack borderWidth={1} p={6}>
                  <Text textStyle={"title"}>Order by</Text>

                  <FormikFieldArray
                    name="orderBy"
                    validateOnChange={true}
                    render={(arrayHelpers) => (
                      <Stack spacing={6}>
                        <Wrap spacing={4}>
                          {props.values.orderBy &&
                            props.values.orderBy.length > 0 &&
                            props.values.orderBy.map((email, index) => (
                              <WrapItem key={index} alignItems="flex-start">
                                <FormQueryOrderByPill
                                  orderNumber={index + 1}
                                  name={`orderBy.${index}`}
                                  onRemove={() => arrayHelpers.remove(index)}
                                  columns={columns}
                                />
                              </WrapItem>
                            ))}
                        </Wrap>

                        <Box>
                          <Button
                            isDisabled={props.values.orderBy.length > 5}
                            size="sm"
                            leftIcon={
                              <MaterialIcon name="add" fontSize="1rem" />
                            }
                            onClick={() => arrayHelpers.push("")}
                          >
                            Add order
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  ></FormikFieldArray>
                </Stack>

                <Stack borderWidth={1} p={6}>
                  <Text textStyle={"title"}>Query Options</Text>

                  <HStack spacing={8}>
                    <Box>
                      <FormSingleCheckbox
                        label={"Sort Desc"}
                        name={"isSortDesc"}
                      >
                        <Text ml={2}>Sort Descending</Text>
                      </FormSingleCheckbox>
                    </Box>
                    <Box>
                      <HStack>
                        <Text>Limit</Text>
                        <FormInputControl
                          name={"limit"}
                          placeholder=""
                          formControlProps={{
                            maxW: "22rem",
                          }}
                          bg="bg.main"
                        />
                      </HStack>
                    </Box>
                  </HStack>
                </Stack>
              </Stack>
            </FormikForm>
          );
        }}
      </Formik>
    );
  }
);
